import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import api from '../../../../services/api';

// Aba "Ocorrências" do Painel de Gestão (Onda 2 · aprovada).
// Pareto de motivos, clientes reincidentes e custo de reentrega.

interface IPareto {
  kpis: {
    failures: number;
    failurePct: number;
    redeliveries: number;
    estimatedCostBRL: number | null;
  };
  reasons: { reason: string; count: number; cumulativePct: number }[];
  repeatCustomers: { name: string; failures: number; topReason: string }[];
}

export const OccurrencesTab = ({ period }: { period: string }) => {
  const [data, setData] = useState<IPareto | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get(
        `/summarizers/occurrence-pareto?period=${period}`,
      );
      setData(response.data);
    } catch {
      setData(null);
    }
  }, [period]);

  useEffect(() => {
    setData(null);
    load();
    timer.current = setInterval(load, 60000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  const maxCount = Math.max(1, ...data.reasons.map((r) => r.count));

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Box display="flex" style={{ gap: 14 }} flexWrap="wrap">
        <Paper style={{ flex: '1 1 200px', padding: '12px 16px' }}>
          <Typography variant="h5" style={{ fontWeight: 800 }}>
            {data.kpis.failures}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            insucessos no período ({data.kpis.failurePct.toLocaleString('pt-BR')}% das entregas)
          </Typography>
        </Paper>
        <Paper style={{ flex: '1 1 200px', padding: '12px 16px' }}>
          <Typography variant="h5" style={{ fontWeight: 800 }}>
            {data.kpis.redeliveries}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            reentregas (insucesso seguido de nova visita entregue)
          </Typography>
        </Paper>
        <Paper style={{ flex: '1 1 200px', padding: '12px 16px' }}>
          <Typography
            variant="h5"
            style={{
              fontWeight: 800,
              color: data.kpis.estimatedCostBRL !== null ? '#C43D3D' : '#999',
            }}
          >
            {data.kpis.estimatedCostBRL !== null
              ? `R$ ${data.kpis.estimatedCostBRL.toLocaleString('pt-BR')}`
              : '—'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            custo estimado de reentrega
            <Tooltip
              title={
                data.kpis.estimatedCostBRL !== null
                  ? 'Reentregas × custo por reentrega definido em Configurações › Parâmetros.'
                  : 'Defina o "Custo por reentrega" em Configurações › Parâmetros para este KPI aparecer.'
              }
              arrow
            >
              <InfoOutlinedIcon
                style={{ fontSize: 14, marginLeft: 4, verticalAlign: 'middle', color: '#999' }}
              />
            </Tooltip>
          </Typography>
        </Paper>
      </Box>

      <Box display="flex" style={{ gap: 16 }} flexWrap="wrap">
        <Paper style={{ flex: '1.5 1 460px', padding: 16 }}>
          <Typography variant="subtitle2" gutterBottom>
            Pareto de motivos — os primeiros em vermelho são onde atacar
          </Typography>
          {data.reasons.map((reason, index) => (
            <Box
              key={reason.reason}
              display="flex"
              alignItems="center"
              style={{ gap: 10 }}
              mb={1.1}
            >
              <span
                style={{
                  width: 170,
                  fontSize: 12.5,
                  color: '#5A6B7A',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={reason.reason}
              >
                {reason.reason}
              </span>
              <div
                style={{
                  flex: 1,
                  background: '#EDF1F5',
                  borderRadius: 4,
                  height: 16,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: '0 auto 0 0',
                    width: `${(reason.count / maxCount) * 100}%`,
                    background:
                      reason.cumulativePct <= 80 || index < 1
                        ? '#C43D3D'
                        : '#0E52A0',
                    borderRadius: 4,
                  }}
                />
              </div>
              <span
                style={{
                  width: 88,
                  textAlign: 'right',
                  fontSize: 12.5,
                  fontWeight: 700,
                }}
              >
                {reason.count}{' '}
                <span style={{ color: '#5A6B7A', fontWeight: 600 }}>
                  · {reason.cumulativePct}%
                </span>
              </span>
            </Box>
          ))}
          {data.reasons.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Nenhum insucesso no período. ✔
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary">
            O % é acumulado — quando os 3 primeiros passam de 80%, resolver
            esses motivos elimina a maior parte do retrabalho.
          </Typography>
        </Paper>

        <Paper style={{ flex: '1 1 320px', padding: 16, alignSelf: 'flex-start' }}>
          <Typography variant="subtitle2" gutterBottom>
            Clientes reincidentes (2+ insucessos)
          </Typography>
          <Table size="small" aria-label="Clientes reincidentes">
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell align="right">Insucessos</TableCell>
                <TableCell>Motivo dominante</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.repeatCustomers.map((customer) => (
                <TableRow key={customer.name}>
                  <TableCell style={{ fontWeight: 600 }}>
                    {customer.name}
                  </TableCell>
                  <TableCell align="right">
                    <span
                      style={{
                        background:
                          customer.failures >= 5 ? '#FAE4E4' : '#FBF0DC',
                        color: customer.failures >= 5 ? '#C43D3D' : '#9A6A0B',
                        fontSize: 11,
                        fontWeight: 800,
                        padding: '2px 9px',
                        borderRadius: 999,
                      }}
                    >
                      {customer.failures}
                    </span>
                  </TableCell>
                  <TableCell>{customer.topReason}</TableCell>
                </TableRow>
              ))}
              {data.repeatCustomers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="textSecondary">
                      Nenhum cliente reincidente no período. ✔
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Typography variant="caption" color="textSecondary">
            Reincidência sugere causa fixa (janela de horário, cadastro de
            endereço) — resolvida uma vez, some do Pareto.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};
